/**
 * useDisplayCurrency — expose la devise d'affichage choisie par le visiteur
 * et des utilitaires pour afficher les prix DANS cette devise.
 *
 * La devise de référence (nativeCurrency) est celle du marchand. Tant que le
 * visiteur n'a rien choisi, l'affichage reste dans la devise du marchand.
 * Dès qu'il choisit une devise, les prix principaux sont convertis.
 */
import { useEffect, useState } from 'react';
import {
  equivalenceText,
  getDisplayCurrency,
  normalizeCurrency,
  setDisplayCurrency,
  loadExchangeRates,
  convertAmount,
  formatMoney,
} from '@/lib/currency';

export function useDisplayCurrency(nativeCurrency = 'MAD') {
  const native = normalizeCurrency(nativeCurrency);
  const [currency, setCurrencyState] = useState(normalizeCurrency(getDisplayCurrency() || native));
  const [, forceRates] = useState(0);

  useEffect(() => {
    // Récupère les taux de change en direct une fois au montage.
    loadExchangeRates();
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const code = (e as CustomEvent<{ currency?: string }>).detail?.currency;
      setCurrencyState(code ? normalizeCurrency(code) : native);
    };
    const onRates = () => forceRates((n) => n + 1);
    window.addEventListener('zifek-currency-changed', handler);
    window.addEventListener('zifek-rates-loaded', onRates);
    return () => {
      window.removeEventListener('zifek-currency-changed', handler);
      window.removeEventListener('zifek-rates-loaded', onRates);
    };
  }, [native]);

  const change = (code: string) => {
    const normalized = normalizeCurrency(code);
    setCurrencyState(normalized);
    setDisplayCurrency(normalized);
  };

  /** Montant converti dans la devise d'affichage (montant d'origine si impossible). */
  const convert = (amount: number, from = native): number => {
    const f = normalizeCurrency(from);
    if (f === currency) return amount;
    const converted = convertAmount(amount, f, currency);
    return converted == null ? amount : converted;
  };

  /** Prix principal formaté dans la devise d'affichage (ex. « 45,30 € »). */
  const display = (amount: number, from = native): string => formatMoney(convert(amount, from), currency);

  /** Montant d'origine formaté dans la devise du marchand (ou null si identique). */
  const original = (amount: number, from = native): string | null => {
    const f = normalizeCurrency(from);
    if (f === currency) return null;
    return formatMoney(amount, f);
  };

  /** Équivalence à afficher (null si identique ou non convertible). */
  const equivalence = (amount: number, from = native): string | null =>
    equivalenceText(amount, from, currency);

  return { currency, change, convert, display, original, equivalence, isForeign: currency !== native, native };
}

export default useDisplayCurrency;